import React, { useState, useEffect, useRef, useMemo } from "react";
import { 
  TextField, 
  Autocomplete, 
  Box, Divider, Typography, IconButton,
  Switch, Button, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import EditPage from "./EditPage";
import { useLoading } from "../../providers/LoadingProvider";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import "dayjs/locale/en-gb";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import ListItemProductRemove from "../../layouts/ListItemProductRemove";
import PetsIcon from "@mui/icons-material/Pets";
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';
import useSupplyStore from "../../store/supplyStore";
import useNavigationHelperStore from "../../store/navigationHelperStore";
import {
  fetchProducts,
  handleError,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateFormatForLocale,
} from "../../utils/lib";
import { useTranslation } from 'react-i18next';
import { useConfig } from "../../providers/ConfigProvider";

const EditSupply = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();
  const { isLoading, setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { config } = useConfig();

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const datePickerFormat = useMemo(
    () => getDayjsDateFormatForLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );

  const { supplies, addSupply, removeSupply, clearSupplies } = useSupplyStore();
  const { state: navState, resetNavigation } = useNavigationHelperStore();
  
  const { pet } = location.state;
  //const [pet, setPet] = useState(location.state || {});
  //console.log(pet);

  const [errors, setErrors] = useState({});
  const [refs, setRefs] = useState({
    product: useRef(null),
    daysInput: useRef(null),
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [addDaysDialogOpen, setAddDaysDialogOpen] = useState(false);
  const [daysToAdd, setDaysToAdd] = useState("");
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [isSupplied, setIsSupplied] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [products, setProducts] = useState([]);
  const [productDisabled, setProductDisabled] = useState(true);

  useEffect(() => {
    setIsLoading(true);

    // if (location.state?.product) {
    //   setSelectedProduct(location.state.product);
    // }

    updateTitle(
      pet.thumb_url,
      pet.name,
      t('edit.newSupplyTitle'),
      false,
      PetsIcon
    );

    toggleFab(false);

    const fetchData = async () => {
      const products = await fetchProducts();
      setProducts(products);

      setIsLoading(false);

      if (refs.product.current) refs.product.current.focus();
    };
    fetchData();
  }, []);

  // Agregar suministro: valida campos y agrega el suministro
  const handleAddSupply = () => {

    if (!selectedProduct) {
      snackbar(t('edit.selectProductSupply'));
      return;
    }

    // Check for duplicate supplies on same date
    if (supplies.some(item => 
      item.product.id === selectedProduct.id && 
      dayjs(item.date).format('YYYY-MM-DD') === dayjs(currentDate).format('YYYY-MM-DD')
    )) {
      snackbar(t('edit.duplicateSupply'));
      return;
    }

    const newSupply = {
      id: Date.now(), // Temporary ID
      product: selectedProduct,
      date: currentDate,
      is_supplied: isSupplied,
    };

    addSupply(newSupply);

    // Reset form
    setSelectedProduct(null);
    //setCurrentDate(dayjs());
    setIsSupplied(false);

    refs.product.current.focus();
  };

  // Remover suministro
  const handleRemoveSupply = (id) => {
    removeSupply(id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supplies || supplies.length === 0) {
      snackbar(t('edit.addAtLeastOneSupply'));
      return;
    }

    save();
  };

  const save = async () => {
    // console.log(pet);

    setIsLoading(true);
    try {
      const supplyToSend = {
        pet: {
          id: pet.id,
          name: pet.name
        },
        supply_array: supplies.map(item => ({
          date_supply: item.is_supplied ? dayjs(item.date).format('YYYY-MM-DD HH:mm:ss') : null,
          date_tentative: dayjs(item.date).format('YYYY-MM-DD HH:mm:ss'),
          product: {
            id: item.product.id,
            name: item.product.name
          }
        }))
      };

      //console.log(supplyToSend);
      
      const response = await axios.post(`${import.meta.env.VITE_API_URL}pets_supply?supply`, supplyToSend, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        }
      });

      navigate('/main/view-pet', { state: { id: pet?.id, updateLastView: false, ...navState?.state } }, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      resetNavigation();
    }
  };

  const handleAddDays = () => {
    if (!daysToAdd || isNaN(daysToAdd)) {
      snackbar(t('edit.validDaysError'));
      return;
    }

    const newDate = dayjs(currentDate).add(parseInt(daysToAdd), 'day');
    setCurrentDate(newDate);
    setAddDaysDialogOpen(false);
    setDaysToAdd("");
  };

  // Modificar el efecto para enfocar el campo de días
  useEffect(() => {
    if (addDaysDialogOpen) {
      // Usar un timeout más largo para asegurar que el diálogo esté completamente renderizado
      const timer = setTimeout(() => {
        if (refs.daysInput.current) {
          refs.daysInput.current.focus();
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [addDaysDialogOpen]);

  // Agregar una función para abrir el diálogo y enfocar el campo
  const handleOpenAddDaysDialog = () => {
    setAddDaysDialogOpen(true);
  };

  return (
    <EditPage 
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      <Box sx={{ width: "100%", marginTop: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <Box display="flex" gap={1} alignItems="center">
            <DatePicker
              label={t('common.date')}
              value={currentDate}
              onChange={(newValue) => setCurrentDate(newValue)}
              views={["year", "month", "day"]}
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (refs.product.current) {
                        refs.product.current.focus();
                      }
                    }
                  }
                } 
              }}
              format={datePickerFormat}
            />
            <IconButton onClick={handleOpenAddDaysDialog}>
              <AddIcon />
            </IconButton>
            {/* <IconButton onClick={() => {}}>
              <CalendarMonthIcon />
            </IconButton> */}
          </Box>
        </LocalizationProvider>
      </Box>

      <Box display="flex" gap={1} alignItems="center">
        <Autocomplete
          fullWidth
          options={products}
          getOptionLabel={(option) => (option ? option.name : "")}
          value={selectedProduct}
          onChange={(e, newValue) => {
            setSelectedProduct(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common.product')}
              name="product"
              margin="normal"
              inputRef={refs.product}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setTimeout(() => {
                    if (selectedProduct) {
                      handleAddSupply();
                    }
                  }, 100);
                }
              }}
    
            />
          )}
        />

        {/* <IconButton onClick={handleSearchProduct}>
          <SearchIcon />
        </IconButton> */}

        {/* <IconButton onClick={handleScanBarcode}>
          <BarcodeScannerIcon />
        </IconButton> */}

        <IconButton onClick={handleAddSupply}>
          <KeyboardReturnIcon />
        </IconButton>
      </Box>

      <Box display="flex" alignItems="center" mt={2}>
        <Switch
          checked={isSupplied}
          onChange={(e) => setIsSupplied(e.target.checked)}
        />
        <Typography 
          onClick={() => setIsSupplied(!isSupplied)}
          sx={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {t('edit.isSupplied')}
        </Typography>
      </Box>

      <Divider sx={{ marginY: 2 }} />

      {/* Supply list */}
      <Box>
        {supplies.map((item) => (
          <ListItemProductRemove
            key={item.id}
            product={item.product.name}
            description={`${dayjs(item.date).format(datePickerFormat)} - ${item.is_supplied ? t('edit.supplied') : t('edit.pending')}`}
            onDelete={() => handleRemoveSupply(item.id)}
            thumbUrl={item.product.thumb_url}
            avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
          />
        ))}
      </Box>

      {/* Add Days Dialog */}
      <Dialog
        open={addDaysDialogOpen}
        onClose={() => setAddDaysDialogOpen(false)}
      >
        <DialogTitle>{t('edit.addDaysTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('edit.howManyDaysToAdd')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('common.days')}
            type="number"
            fullWidth
            value={daysToAdd}
            onChange={(e) => setDaysToAdd(e.target.value)}
            inputRef={refs.daysInput}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDaysDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleAddDays}>{t('common.accept')}</Button>
        </DialogActions>
      </Dialog>
    </EditPage>
  );
};

export default EditSupply;
